/**
 * gt-diff.ts — the PURE typed differ recovered -> corrected for the JudgeCritiqueStore
 * (Epic gridline-marry-refinement Phase J5a).
 *
 * Design: docs/epics/active/gridline-marry-refinement/phase-J5-design-260817.md
 * §4.1 (the differ is pure + reuses the eval IoU matcher) + §3.1 (the axis table).
 * Authoring home (§T): TS next to ground-truth-doc.ts / gt-score.ts / gt-mutate.ts.
 *
 * WHY reuse the eval matcher (A.6 unify / rule #11 / INV-CRIT-REUSE-EXPORT). Node
 * correspondence between `recovered` and `corrected` is the SAME problem the eval
 * scorer already solves: match by geometry-IoU + role. So this differ REUSES
 * `alignByIoU` from gt-score.ts (which itself reuses the canonical `bboxIoU` from
 * ground-truth-doc.ts — INV-EVAL-2) rather than growing a second matcher. It adds
 * ONLY the per-pair field comparison + the design §3.1 axis derivation on top.
 *
 * PURE + Node-importable: type-only editor imports + runtime imports of the reused
 * matcher/geometry helpers; no DOM globals at module scope.
 *
 * @module editor/gt-diff
 */
import { alignByIoU, clampBbox, containmentOfExtract, DEFAULT_RUBRIC, SMEAR_AREA_FLOOR, } from "./gt-score.js";
/**
 * IoU at/above which a matched pair's geometry is treated as UNCHANGED (identical
 * re-serialization yields exactly 1.0). Below this — but still matched (IoU >= tau) —
 * the node moved/resized: a `geometry-changed` delta. The 0.98 slack absorbs
 * float-rounding on genuinely-unchanged boxes without swallowing a real nudge.
 */
export const GEOMETRY_IDENTITY_IOU = 0.98;
/**
 * Containment at/above which an ADDED node is considered to sit INSIDE a deleted
 * whole-page Figure — the split-into-regions signal (StructuralMisroute). Reuses
 * `containmentOfExtract(figure, added)` = fraction of the added box inside the figure.
 */
const MISROUTE_CONTAINMENT = 0.7;
function isHeadingRole(role) {
    return /^H[1-6]$/.test(role);
}
function area(b) {
    return Math.max(0, b.w) * Math.max(0, b.h);
}
/** Adapt a corrected-side GroundTruthElement to the eval scorer's ExtractElement shape. */
function toExtract(e) {
    return {
        id: e.gtId,
        role: e.role,
        bbox: e.bbox,
        readingOrderIndex: e.readingOrderIndex,
        parentId: e.parentGtId,
        // Threads the corrected-side element's real page through so the reused
        // matcher's page-partition gate (INV-EVAL-11, gt-score.ts) applies here too —
        // a recovered-side node should never align to a corrected-side node on a
        // different page.
        page: e.page,
    };
}
/**
 * Axis for a `role-changed` delta, from the role transition (design §3.1 table). The
 * special roles win; a P<->Caption<->Note refinement (no dedicated judge axis) falls
 * back to `Heading` (the block-structure axis) — the reviewer disambiguates via an
 * OPTIONAL annotation when that is wrong.
 */
function roleChangedAxis(before, after) {
    if (isHeadingRole(before) || isHeadingRole(after))
        return "Heading";
    if (before === "L" || after === "L")
        return "List";
    if (before === "Formula" || after === "Formula")
        return "Math";
    if (before === "Table" || after === "Table")
        return "Table";
    if (before === "Figure" || after === "Figure")
        return "FigureAlt";
    if (before === "Artifact" || after === "Artifact" || before === "Span" || after === "Span")
        return "NoLeak";
    return "Heading";
}
/**
 * Compute the typed diff recovered -> corrected. Reuses the eval IoU matcher for node
 * correspondence, then classifies each matched-pair field change + each unmatched node.
 * `tau` is the IoU alignment floor (default `DEFAULT_RUBRIC.tauMatch` = 0.5) — a node
 * resized past that floor reads as delete+add (a re-route), not a same-node nudge.
 */
export function diffGroundTruth(recovered, corrected, tau = DEFAULT_RUBRIC.tauMatch) {
    const rec = recovered.elements;
    const cor = corrected.elements;
    const recById = new Map(rec.map((e) => [e.gtId, e]));
    const corById = new Map(cor.map((e) => [e.gtId, e]));
    // Node correspondence via the REUSED eval matcher (recovered = "gt" side,
    // corrected = "extract" side). matched.gtId is a recovered gtId; matched.extractId
    // is a corrected gtId. missedGt = deleted recovered nodes; spuriousExtract = added
    // corrected nodes.
    const { matched, missedGt, spuriousExtract } = alignByIoU(rec, cor.map(toExtract), tau);
    // recovered gtId -> corrected gtId, for reparenting resolution.
    const recToCor = new Map(matched.map((m) => [m.gtId, m.extractId]));
    // Reading-order ranks over matched pairs only (robust to add/delete shifting the
    // absolute readingOrderIndex): a node whose RANK among matched pairs differs
    // between the two sides moved in reading order.
    const recRank = new Map();
    [...matched]
        .sort((a, b) => (recById.get(a.gtId)?.readingOrderIndex ?? 0) - (recById.get(b.gtId)?.readingOrderIndex ?? 0))
        .forEach((m, i) => recRank.set(m.gtId, i));
    const corRank = new Map();
    [...matched]
        .sort((a, b) => (corById.get(a.extractId)?.readingOrderIndex ?? 0) - (corById.get(b.extractId)?.readingOrderIndex ?? 0))
        .forEach((m, i) => corRank.set(m.extractId, i));
    const deltas = [];
    for (const m of matched) {
        const r = recById.get(m.gtId);
        const c = corById.get(m.extractId);
        if (!r || !c)
            continue;
        // The corrected gtId is the stable key for a matched/added node.
        const key = c.gtId;
        if (r.role !== c.role) {
            deltas.push({ gtId: key, kind: "role-changed", axis: roleChangedAxis(r.role, c.role), before: r.role, after: c.role });
        }
        if ((recRank.get(m.gtId) ?? 0) !== (corRank.get(m.extractId) ?? 0)) {
            deltas.push({
                gtId: key, kind: "reading-order-changed", axis: "ReadingOrder",
                before: r.readingOrderIndex, after: c.readingOrderIndex,
            });
        }
        if (m.iou < GEOMETRY_IDENTITY_IOU) {
            // A region-boundary correction — the marry whole-page-box class (§5.2).
            deltas.push({ gtId: key, kind: "geometry-changed", axis: "StructuralMisroute", before: r.bbox, after: c.bbox });
        }
        if (r.alt !== c.alt) {
            deltas.push({ gtId: key, kind: "alt-changed", axis: "FigureAlt", before: r.alt, after: c.alt });
        }
        if (r.actualText !== c.actualText) {
            deltas.push({
                gtId: key, kind: "actual-text-changed",
                axis: c.role === "Formula" ? "Math" : "FigureAlt",
                before: r.actualText, after: c.actualText,
            });
        }
        // Reparenting: the recovered parent must map (via the matcher) to the corrected
        // node's actual parent; a mismatch (incl. one-sided null) is a reparent.
        const mappedRecParent = r.parentGtId !== null ? (recToCor.get(r.parentGtId) ?? null) : null;
        if (mappedRecParent !== c.parentGtId) {
            deltas.push({
                gtId: key, kind: "reparented",
                axis: c.parentGtId !== null && corById.get(c.parentGtId)?.role === "L" ? "List" : "StructuralMisroute",
                before: r.parentGtId, after: c.parentGtId,
            });
        }
    }
    // Deleted recovered nodes. A large deleted Figure is the whole-page-box the human
    // split into regions (StructuralMisroute); an Artifact/Span deletion is a leak
    // removal (NoLeak); otherwise over-segmentation (Completeness).
    const misrouteFigures = [];
    for (const gtId of missedGt) {
        const r = recById.get(gtId);
        if (!r)
            continue;
        let axis;
        const rb = clampBbox(r.bbox);
        if (r.role === "Figure" && area(rb) >= SMEAR_AREA_FLOOR) {
            axis = "StructuralMisroute";
            misrouteFigures.push(rb);
        }
        else if (r.role === "Artifact" || r.role === "Span") {
            axis = "NoLeak";
        }
        else {
            axis = "Completeness";
        }
        deltas.push({ gtId, kind: "node-deleted", axis, before: { role: r.role, bbox: r.bbox } });
    }
    // Added corrected nodes. A region falling inside a deleted whole-page Figure is a
    // split region (StructuralMisroute); otherwise under-segmentation (Completeness).
    for (const gtId of spuriousExtract) {
        const c = corById.get(gtId);
        if (!c)
            continue;
        const cb = clampBbox(c.bbox);
        const insideMisroute = misrouteFigures.some((f) => containmentOfExtract(f, cb) >= MISROUTE_CONTAINMENT);
        const axis = insideMisroute ? "StructuralMisroute" : "Completeness";
        deltas.push({ gtId, kind: "node-added", axis, after: { role: c.role, bbox: c.bbox } });
    }
    return deltas;
}
/**
 * Derive the pairwise oracle from a typed diff: one `corrected wins` verdict per unique
 * axis the human touched, plus one `overall` verdict when any delta exists (design §2 —
 * the corrected structure is, on every touched axis, superior to the recovered one).
 */
export function deriveVerdicts(deltas) {
    const axes = new Set();
    for (const d of deltas)
        axes.add(d.axis);
    const verdicts = [...axes].map((axis) => ({ axis, winner: "corrected" }));
    if (deltas.length > 0)
        verdicts.push({ axis: "overall", winner: "corrected" });
    return verdicts;
}
/** Convenience: the typed diff AND its derived pairwise oracle in one call. */
export function diffAndVerdict(recovered, corrected, tau) {
    const delta = diffGroundTruth(recovered, corrected, tau);
    return { delta, derivedVerdict: deriveVerdicts(delta) };
}
